import {
    AnchorButton,
    Button,
    Dialog,
    DialogBody,
    DialogFooter
} from "@blueprintjs/core";
import React from "react";

import { IconNames } from "@blueprintjs/icons";
import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { isPricedConstraintConfig } from "types/componentConfigPerRegion";
import { hasMultipleScopesForRegionAndGeneratorType } from "types/scope";
import { useIsUnlimitedSubscription } from "../utils/useIsUnlimitedSubscription";
import VoltageSelectWrapper from "../VoltageSelectWrapper";
import AllocatedCostFilter from "./AllocatedCostFilter";
import ColorModeFilter from "./ColorModeFilter";
import ColorThresholdsFilter from "./ColorThresholdsFilter";
import FavoritesFilter from "./FavoritesFilter";
import FiltersDialogSection from "./FiltersDialogSection";
import FilterWrapper from "./FilterWrapper";
import LocationFilter from "./LocationFilter";
import LowConfidenceBusFilter from "./LowConfidenceBusFilter";
import OutOfRegionBusFilter from "./OutOfRegionBusFilter";
import OwnersFilter from "./OwnersFilter";
import PreexistingFilter from "./PreexistingFilter";
import ScopeSelectFilter from "./ScopeSelectFilter";
import TotalCostFilter from "./TotalCostFilter";
import UnlockedBusesFilter from "./UnlockedBusesFilter";

type ScopeSelectorDialogProps = {
    readonly isOpen: boolean;
    readonly closeDialog: () => void;
};

const ScopeSelectorDialog: React.FC<ScopeSelectorDialogProps> = (props) => {
    const { isOpen, closeDialog } = props;
    const { componentConfig, region, generator } = useMapViewRoutingMetadata();
    const { scopes } = useUserDataContext();

    const {
        busFiltersConfiguration: { resetToDefaults, filtersDisabled }
    } = useMapViewConfiguration();
    const { setFavoritesFilterOn } = useFavoriteBuses();

    // Clears the favorites-only filter along with busFilters.
    const resetAllFilters = (): void => {
        resetToDefaults();
        setFavoritesFilterOn(false);
    };

    const isPricedConfig = isPricedConstraintConfig(componentConfig);
    const isAllocatedCostsEnabled =
        isPricedConfig && componentConfig.hasAllocatedCost;

    const showOwnersFilter = componentConfig.showOwnersFilter;

    const lowConfidenceBusFilterEnabled =
        componentConfig.allowUsersToViewLowConfidenceBuses;
    const outOfRegionBusFilterEnabled =
        componentConfig.showOutOfRegionBusFilter ?? false;
    const unlockedBusesFilterEnabled = !useIsUnlimitedSubscription();

    const scopeFilterEnabled = hasMultipleScopesForRegionAndGeneratorType(
        scopes,
        region,
        generator
    );
    const isPreExistingConstraintsEnabled =
        isPricedConfig && componentConfig.isPreExistingConstraintsEnabled;
    const showQueueAssumptionsSection =
        scopeFilterEnabled || isPreExistingConstraintsEnabled;

    return (
        <Dialog
            isOpen={isOpen}
            title={"All filters"}
            onClose={closeDialog}
            icon={IconNames.FILTER}
        >
            <DialogBody>
                <FiltersDialogSection header="Voltages">
                    <FilterWrapper>
                        <VoltageSelectWrapper />
                    </FilterWrapper>
                </FiltersDialogSection>
                {showOwnersFilter && (
                    <FiltersDialogSection header="Owners">
                        <FilterWrapper>
                            <OwnersFilter />
                        </FilterWrapper>
                    </FiltersDialogSection>
                )}
                {showQueueAssumptionsSection && (
                    <FiltersDialogSection header="Queue assumptions">
                        {scopeFilterEnabled && (
                            <ScopeSelectFilter inSidebar={false} />
                        )}
                        {isPreExistingConstraintsEnabled && (
                            <PreexistingFilter />
                        )}
                    </FiltersDialogSection>
                )}
                {isPricedConfig && (
                    <FiltersDialogSection header="Maximum costs">
                        {isAllocatedCostsEnabled && <AllocatedCostFilter />}
                        <TotalCostFilter />
                    </FiltersDialogSection>
                )}
                <FiltersDialogSection header="Color thresholds">
                    <ColorModeFilter />
                    <ColorThresholdsFilter />
                </FiltersDialogSection>
                {componentConfig.hasPlannedInterconnectionLocations && (
                    <FiltersDialogSection header="Location type">
                        <LocationFilter />
                    </FiltersDialogSection>
                )}
                <FiltersDialogSection header="Other">
                    {lowConfidenceBusFilterEnabled && (
                        <LowConfidenceBusFilter />
                    )}
                    {outOfRegionBusFilterEnabled && <OutOfRegionBusFilter />}
                    {unlockedBusesFilterEnabled && <UnlockedBusesFilter />}
                    <FavoritesFilter
                        inSidebar={false}
                        onOpenPanel={closeDialog}
                    />
                </FiltersDialogSection>
            </DialogBody>
            <DialogFooter
                actions={
                    <AnchorButton intent="primary" onClick={closeDialog}>
                        Done
                    </AnchorButton>
                }
            >
                <Button
                    minimal={true}
                    onClick={resetAllFilters}
                    disabled={filtersDisabled}
                >
                    Reset all
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default ScopeSelectorDialog;
